import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../../shared/components/SEO';
import { BRAND_SANS_FONT } from '../../shared/design/theme';
import { LandingContainer, SectionTitle, SectionSubtitle } from '../home/HomePage.styles';
import '../home/HomePage.styles.css';
import { Footer } from '../../shared/components/Footer';
import { SiteHeader } from '../../shared/components/SiteHeader';
import { ACCENT, INK } from '../../shared/components/DeckVisuals';
import ComparisonSlide from '../../shared/components/ComparisonSlide';
import WorkflowComparison from './WorkflowComparison';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import HubOutlined from '@mui/icons-material/HubOutlined';
import StyleOutlined from '@mui/icons-material/StyleOutlined';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import AutorenewRounded from '@mui/icons-material/AutorenewRounded';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';

/**
 * /vs/claude - the comparison page for buyers who already use Claude.
 *
 * Buyer-first build (2026-07-11): a Dhruv-class reader does not need Claude
 * taxonomized - he needs what sits around the model and one example,
 * readable in 30 seconds. Claims are truth-checked against Anthropic's
 * current docs: no capability contrasts Claude has since closed, no
 * competitor branding on the fabricated weak slide, no head-to-head claims.
 *
 * SPINE CORRECTED 2026-08-11. The page used to argue "same model, different
 * machine", which was true for exactly as long as Restack ran Claude and only
 * Claude. The shipped picker now spans several labs
 * (src/screens/chat/ai-chat/constants.js) and the model is switchable between
 * turns inside one chat, so "same model" became the one claim on this page
 * that is false - and it was throwing away the strongest argument against a
 * single-lab agent. Three axes now, in order of novelty:
 *
 *   1. WHERE the work happens. The deck the reader already has open, edited
 *      in place, versus a round trip that hands back a second file to
 *      reconcile.
 *   2. WHICH MODEL runs it. Claude Cowork is Anthropic only by definition.
 *      Restack lets the user re-run the same slide through a different lab,
 *      which is a real second opinion for a team pressure-testing a
 *      storyline. Model choice is a capability here, never the lead.
 *   3. THE MACHINE. The seven cards. Unchanged, and still the bulk of it.
 *
 * Beats, in order:
 *   1. HERO. You already use Claude. So do we. (Not an argument about the
 *      model: about where the work happens and the machine around it.)
 *   2. SWAP THE MODEL, KEEP THE MACHINE. The seven things built around the
 *      model, as a card grid.
 *   3. THE WORKFLOW DIAGRAM. WorkflowComparison with competitor="cowork":
 *      the round trip against the open file, plus the model pick.
 *   4. WHAT IT LOOKS LIKE IN PRACTICE. The same brief at two altitudes:
 *      an unbranded competent generic draft vs the deal-team slide, both
 *      labelled illustrative, with the expectation line under them.
 *   5. MAKING THE CASE. Four internal-review questions - the fourth is the
 *      honest "when is Claude alone enough" concession.
 *
 * Then the standing CTA. The generic /vs/<slug> table page (ComparePage)
 * still serves the other competitors.
 */

const META_TITLE = 'Restack vs Claude | Private Equity deliverables';
const META_DESCRIPTION =
  'Compare Restack with Claude Chat, Claude Cowork, and Claude for Microsoft 365 for recurring private equity deliverables.';

// SECTION 2 - the seven things built around the model. Bodies stay short:
// one glance per card. These are the machine, they are the reason the page
// exists, and the 2026-08-11 spine correction deliberately left every body
// untouched: only the framing above them was wrong.
const MACHINE = [
  {
    title: 'Built around the deliverable your team runs every cycle',
    Icon: AutorenewRounded,
    body: 'The quarterly board pack, the survey wave, the IC memo. Point it at the last version and the new data; it rebuilds the pages in place, figures re-checked against sources. A general agent helps you edit; Restack runs the cycle.',
  },
  {
    title: 'Speaks Private Equity by default',
    Icon: AccountTreeOutlined,
    body: 'Action titles, IC structure, TAM/SAM/SOM patterns, waterfall, bridge, and Mekko charts - how it builds without being told, because the work has one right grammar.',
  },
  {
    title: 'Model to memo in one instruction',
    Icon: HubOutlined,
    body: "New data lands in the Excel model; the deck pages and the memo follow from one instruction, on your templates. The point isn't three apps - it's that the deliverable spans them and the rebuild shouldn't.",
  },
  {
    title: 'Defensible in the room',
    Icon: FactCheckOutlined,
    body: "It screenshots what it built, checks layout and figures against sources, and flags what doesn't reconcile instead of shipping it. When the committee asks where a number came from, the answer is one click.",
  },
  {
    title: 'Learns your format from your deck',
    Icon: StyleOutlined,
    body: 'One reference slide plus your master and theme guide every build. Complex templates still get a human pass.',
  },
  {
    title: 'Works inside your app, on the live file',
    Icon: EditOutlined,
    body: "Surgical edits to the open file's underlying XML - shapes, charts, masters - not a regenerated file. One working file from draft to review.",
  },
  {
    title: 'Firm context that accrues',
    Icon: Inventory2Outlined,
    body: 'Engagement-scoped context lets the agent draw on your templates and reference material as it builds, so the context accrues from the work.',
  },
];

// Scroll-reveal wrapper: fades and lifts children in the first time they
// enter the viewport. Skips the animation entirely for reduced-motion users
// and environments without IntersectionObserver.
function Reveal({ delay = 0, sx, children }) {
  const ref = React.useRef(null);
  const [shown, setShown] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof IntersectionObserver === 'undefined' || reduced) {
      setShown(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <Box
      ref={ref}
      sx={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'translateY(18px)',
        transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: META_TITLE,
    description: META_DESCRIPTION,
    author: { '@type': 'Organization', name: 'Restack', url: 'https://re-stack.com/' },
    publisher: {
      '@type': 'Organization',
      name: 'Restack',
      logo: { '@type': 'ImageObject', url: 'https://re-stack.com/logo.svg' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://re-stack.com/vs/claude' },
    about: [
      { '@type': 'SoftwareApplication', name: 'Restack', url: 'https://re-stack.com/' },
      { '@type': 'SoftwareApplication', name: 'Claude' },
    ],
  },
];

export default function ClaudeComparePage() {
  const hair = alpha(INK, 0.08);

  const sectionHeadingSx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: { xs: '1.05rem', md: '1.15rem' },
    fontWeight: 600,
    letterSpacing: '-0.015em',
    color: 'text.primary',
  };
  const bodySx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: '0.84rem',
    fontWeight: 400,
    lineHeight: 1.6,
    color: 'text.secondary',
  };
  const h2Sx = {
    textAlign: 'center',
    fontSize: { xs: '1.5rem', md: '2rem' },
    fontWeight: 600,
    lineHeight: 1.2,
  };
  const eyebrowSx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: ACCENT,
    mb: 1.5,
  };
  const sectionSx = {
    mt: { xs: 5, md: 7 },
    pt: { xs: 4, md: 5 },
    borderTop: `1px solid ${hair}`,
  };
  // Shared card chrome for the text-only card surfaces.
  const cardSx = {
    borderRadius: '14px',
    border: `1px solid ${hair}`,
    background: alpha(INK, 0.015),
    p: { xs: 2.25, md: 2.75 },
  };
  const cardTitleSx = {
    fontFamily: BRAND_SANS_FONT,
    fontWeight: 600,
    fontSize: { xs: '0.95rem', md: '1rem' },
    letterSpacing: '-0.01em',
    color: 'text.primary',
  };
  // The seven machine cards get a touch more visual weight than the plain
  // card: an accent icon chip and a gentle lift on hover.
  const machineCardSx = {
    ...cardSx,
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      borderColor: alpha(ACCENT, 0.25),
      boxShadow: `0 8px 24px ${alpha(INK, 0.07)}`,
    },
  };
  const iconChipSx = {
    width: 34,
    height: 34,
    borderRadius: '9px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: alpha(ACCENT, 0.08),
    mb: 1.25,
  };
  // Emphasized full-width line (the expectation line under the panels).
  const pullLineSx = {
    mt: { xs: 2.5, md: 3 },
    textAlign: 'center',
    maxWidth: 820,
    mx: 'auto',
    fontFamily: BRAND_SANS_FONT,
    fontSize: { xs: '0.9rem', md: '0.95rem' },
    fontWeight: 500,
    lineHeight: 1.6,
    color: 'text.primary',
  };
  const primaryBtn = {
    textTransform: 'none',
    fontFamily: BRAND_SANS_FONT,
    fontWeight: 600,
    fontSize: '0.85rem',
    letterSpacing: '-0.005em',
    borderRadius: '999px',
    px: 2.5,
    py: 0.9,
    color: '#FFFFFF',
    background: 'linear-gradient(135deg, #B83B1D 0%, #D4694E 100%)',
    boxShadow: '0 4px 14px rgba(184, 59, 29, 0.22)',
    '&:hover': {
      background: 'linear-gradient(135deg, #A63419 0%, #C25A42 100%)',
      boxShadow: '0 6px 18px rgba(184, 59, 29, 0.3)',
    },
  };

  const DemoCta = ({ label }) => (
    <Button
      component={RouterLink}
      to="/demo"
      endIcon={<ArrowForwardRounded sx={{ fontSize: '1rem' }} />}
      sx={primaryBtn}
    >
      {label}
    </Button>
  );

  // A labelled frame above each finished ComparisonSlide. The pill names the
  // side; the note (optional) frames the contrast. The left pill is
  // deliberately NOT branded with a competitor's name: it labels the
  // fabricated weak output as what it is, a competent generic draft.
  const FrameLabel = ({ label, note, muted, children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignSelf: 'flex-start',
            alignItems: 'center',
            gap: 0.6,
            maxWidth: '100%',
            px: 1,
            py: 0.35,
            borderRadius: '999px',
            background: muted ? alpha(INK, 0.05) : alpha(ACCENT, 0.1),
            color: muted ? alpha(INK, 0.6) : ACCENT,
            fontFamily: BRAND_SANS_FONT,
            fontSize: '0.66rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            lineHeight: 1.4,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              flexShrink: 0,
              background: muted ? alpha(INK, 0.3) : ACCENT,
            }}
          />
          {label}
        </Box>
        {note && (
          <Typography
            sx={{
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.78rem',
              fontWeight: 500,
              color: muted ? alpha(INK, 0.5) : alpha(ACCENT, 0.85),
            }}
          >
            {note}
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  );


  return (
    <LandingContainer data-theme="light" sx={{ minHeight: '100vh' }}>
      <SEO
        title={META_TITLE}
        description={META_DESCRIPTION}
        canonical="/vs/claude"
        ogType="article"
        schema={SCHEMA}
      />

      <SiteHeader />

      <Box
        sx={{
          maxWidth: 1280,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 'calc(56px + 1.5rem)', md: 'calc(64px + 2.5rem)' },
          pb: { xs: 6, md: 9 },
          '& strong': { color: 'text.primary', fontWeight: 600 },
        }}
      >
        {/* HERO. The opener stays disarming on purpose: the reader does use
            Claude, so does Restack, and a hostile page would lose him on the
            first line. The sub is what changed - it used to say "Restack runs
            on Claude", which conceded the model as identical and is no longer
            true. It now names all three axes in order of novelty (where the
            work happens, which model runs it, the machine) and the seven cards
            below itemize the third. A soft accent glow + comparison pill give
            the page its website chrome. */}
        <Box sx={{ position: 'relative', textAlign: 'center', maxWidth: 820, mx: 'auto' }}>
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: -140,
              left: '50%',
              transform: 'translateX(-50%)',
              width: { xs: 360, md: 680 },
              height: 340,
              borderRadius: '50%',
              background: `radial-gradient(closest-side, ${alpha(ACCENT, 0.09)}, transparent)`,
              filter: 'blur(12px)',
              pointerEvents: 'none',
            }}
          />
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.7,
                px: 1.4,
                py: 0.5,
                mb: 2,
                borderRadius: '999px',
                border: `1px solid ${alpha(ACCENT, 0.25)}`,
                background: alpha(ACCENT, 0.06),
              }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: ACCENT }} />
              <Typography
                sx={{
                  fontFamily: BRAND_SANS_FONT,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: ACCENT,
                }}
              >
                Restack vs Claude
              </Typography>
            </Box>
            <SectionTitle component="h1" sx={{ mb: 2, textAlign: 'center' }}>
              You already use Claude. <em>So do we.</em>
            </SectionTitle>
            <SectionSubtitle sx={{ mx: 'auto', mb: 0, maxWidth: { xs: '100%', md: 760 } }}>
              Restack opens on Claude by default, so this is not an argument
              about whose model is better. The work happens in the deck you
              already have open, not in a copy that goes out to a separate
              app and comes back.
              You pick which model runs it, so a storyline that has to survive
              a committee can be pressure-tested by a second lab. Everything
              else here is the machine around both.
            </SectionSubtitle>
          </Box>
        </Box>

        {/* SECTION 2 - SWAP THE MODEL, KEEP THE MACHINE: the seven things built
            around the model, in a responsive card grid. The heading used to
            read "Same model. Different machine." and had to go: the model is
            now the switchable part and the machine is the constant, which is
            the same argument with the false half removed. Nothing below the
            heading changed - the cards are the machine and they were right. */}
        <Box component="section" sx={sectionSx}>
          <Reveal>
            <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto', mb: { xs: 3, md: 4 } }}>
              <Typography component="div" sx={eyebrowSx}>
                What sits around the model you pick
              </Typography>
              <SectionTitle component="h2" sx={h2Sx}>
                Swap the model. Keep the <em>machine.</em>
              </SectionTitle>
            </Box>
          </Reveal>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: { xs: 2, md: 2.5 },
              maxWidth: 1180,
              mx: 'auto',
            }}
          >
            {MACHINE.map((item, i) => (
              <Reveal
                key={item.title}
                delay={i * 70}
                sx={{
                  height: '100%',
                  gridColumn: { md: i === MACHINE.length - 1 ? '1 / -1' : 'auto' },
                }}
              >
                <Box sx={{ ...machineCardSx, height: '100%' }}>
                  <Box sx={iconChipSx}>
                    <item.Icon sx={{ fontSize: '1.15rem', color: ACCENT }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography component="h3" sx={cardTitleSx}>
                      {item.title}
                    </Typography>
                    {item.status && (
                      <Box
                        component="span"
                        sx={{
                          px: 0.9,
                          py: 0.25,
                          borderRadius: '999px',
                          border: `1px solid ${alpha(ACCENT, 0.2)}`,
                          background: alpha(ACCENT, 0.07),
                          color: ACCENT,
                          fontFamily: BRAND_SANS_FONT,
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          lineHeight: 1.4,
                        }}
                      >
                        {item.status}
                      </Box>
                    )}
                  </Box>
                  <Typography sx={bodySx}>{item.body}</Typography>
                </Box>
              </Reveal>
            ))}
          </Box>
        </Box>

        {/* The round trip versus editing the open file, plus the model pick.
            Sits after the machine cards so the reader has the argument before
            the diagram, and before the two-altitudes panel. competitor="cowork"
            is the whole API: the component owns its own framing, this page
            supplies nothing else and reads none of its internals. */}
        <Box component="section" sx={sectionSx}>
          <WorkflowComparison competitor="cowork" />
        </Box>


        {/* SECTION 3 - WHAT IT LOOKS LIKE IN PRACTICE. The same brief at two
            altitudes: the unbranded competent generic draft against the
            deal-team slide, labelled as illustrative mocks, with the
            expectation line under both panels. */}
        <Box
          component="section"
          id="comparison"
          sx={{
            scrollMarginTop: { xs: '76px', md: '92px' },
            ...sectionSx,
          }}
        >
          <Reveal>
            <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto', mb: { xs: 3.5, md: 4.5 } }}>
              <Typography component="div" sx={eyebrowSx}>
                The same brief, two altitudes
              </Typography>
              <SectionTitle component="h2" sx={h2Sx}>
                What it looks like in <em>practice.</em>
              </SectionTitle>
              <SectionSubtitle sx={{ mx: 'auto', mb: 0, fontSize: { xs: '0.9rem', md: '0.95rem' } }}>
                A generic tool builds a competent deck. Restack builds to the
                depth a committee expects.
              </SectionSubtitle>
            </Box>
          </Reveal>

          {/* The two finished slides, side by side; stack on xs. A gentle
              hover lift keeps them feeling like objects, not screenshots. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
              gap: { xs: 3.5, md: 3 },
              alignItems: 'start',
              maxWidth: 1180,
              mx: 'auto',
              '& > *': { minWidth: 0 },
            }}
          >
            <Reveal>
              <FrameLabel label="A competent generic draft" muted>
                <ComparisonSlide
                  variant="generalised"
                  accent={INK}
                  pageNum={4}
                  sx={{ transition: 'transform 0.25s ease', '&:hover': { transform: 'translateY(-4px)' } }}
                />
              </FrameLabel>
            </Reveal>

            <Reveal delay={140}>
              <FrameLabel label="Restack - built for deal teams">
                <ComparisonSlide
                  variant="specialised"
                  accent={ACCENT}
                  pageNum={4}
                  sx={{ transition: 'transform 0.25s ease', '&:hover': { transform: 'translateY(-4px)' } }}
                />
              </FrameLabel>
            </Reveal>
          </Box>

          {/* Prominent honesty label: the slides are mocks, and the citations
              on the specialised side are not real sources. */}
          <Typography
            sx={{
              mt: { xs: 2, md: 2.5 },
              textAlign: 'center',
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.78rem',
              fontWeight: 500,
              color: alpha(INK, 0.55),
            }}
          >
            Illustrative example - figures and sources are mocked for demonstration.
          </Typography>

          <Typography sx={pullLineSx}>
            What to expect: Restack targets complete data and factual accuracy
            and roughly 85 percent formatting polish. Your team reviews and
            finishes the deliverable - the goal is turning a rebuild into a
            review.
          </Typography>
        </Box>

        {/* SECTION 4 - MAKING THE CASE. Four internal-review questions; the
            fourth is the honest concession that makes the other three read
            as straight answers. */}
        <Box component="section" sx={sectionSx}>
          <Reveal>
            <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto', mb: { xs: 3, md: 4 } }}>
              <Typography component="div" sx={eyebrowSx}>
                The questions every review asks
              </Typography>
              <SectionTitle component="h2" sx={h2Sx}>
                Making the <em>case.</em>
              </SectionTitle>
            </Box>
          </Reveal>

          <Reveal>
          <Box sx={{ ...cardSx, maxWidth: 820, mx: 'auto' }}>
            {[
              {
                question: '"We already pay for Claude."',
                answer: (
                  <>
                    Keep it - the two do different jobs. Claude is where your
                    team thinks; Restack is where the recurring deliverable
                    gets built, in the file that is already open. Which model
                    runs it is a setting inside Restack, Claude included, so
                    this is not a second model subscription to argue about:
                    the tokens your team already spends on deck work move to
                    the tool built for that job. The test is whether the next
                    cycle becomes a review instead of another rebuild.
                    $800 per seat per month, month to month, no lock-in.
                  </>
                ),
              },
              {
                question: '"Will the team actually use it?"',
                answer: (
                  <>
                    It deploys as a Microsoft 365 add-in and runs from the open
                    file - no separate workspace to adopt, no version forks.
                    The honest way to find out: pick one recurring deliverable
                    and run a full cycle with the people who build it.
                  </>
                ),
              },
              {
                question: '"What does IT need to know?"',
                answer: (
                  <>
                    No training on your data. Sandboxed code execution, Entra
                    ID SSO, centralized Microsoft 365 deployment. Data
                    handling, retention, and current compliance status:{' '}
                    <Box
                      component={RouterLink}
                      to="/security"
                      sx={{
                        color: ACCENT,
                        fontWeight: 600,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      re-stack.com/security
                    </Box>
                    .
                  </>
                ),
              },
              {
                question: '"When is Claude alone enough?"',
                answer: (
                  <>
                    When the work is research and storyline and the team is
                    happy building the deck itself. Restack earns a look when a
                    recurring deliverable eats real hours every cycle and has
                    to land at committee grade - sourced, on template,
                    defensible in the room.
                  </>
                ),
              },
            ].map((block, i, arr) => (
              <Box
                key={block.question}
                sx={{
                  pt: i === 0 ? 0 : 2.25,
                  pb: i === arr.length - 1 ? 0 : 2.25,
                  borderBottom: i === arr.length - 1 ? 'none' : `1px solid ${hair}`,
                }}
              >
                {/* The question as a short pull-quote. */}
                <Typography
                  component="h3"
                  sx={{
                    fontFamily: BRAND_SANS_FONT,
                    fontSize: { xs: '1.05rem', md: '1.15rem' },
                    fontWeight: 600,
                    letterSpacing: '-0.015em',
                    color: 'text.primary',
                    mb: 0.75,
                  }}
                >
                  {block.question}
                </Typography>
                <Typography sx={bodySx}>{block.answer}</Typography>
              </Box>
            ))}
          </Box>
          </Reveal>
        </Box>

        {/* Closing - the standing CTA in an accent-tinted band so the page
            ends on a visual note, not a bare paragraph. */}
        <Box
          component="section"
          sx={{
            ...sectionSx,
            maxWidth: 820,
            mx: 'auto',
          }}
        >
          <Reveal>
            <Box
              sx={{
                textAlign: 'center',
                borderRadius: '18px',
                border: `1px solid ${alpha(ACCENT, 0.16)}`,
                background: `linear-gradient(180deg, ${alpha(ACCENT, 0.05)} 0%, ${alpha(ACCENT, 0.02)} 100%)`,
                px: { xs: 2.5, md: 5 },
                py: { xs: 3.5, md: 4.5 },
              }}
            >
              <Typography component="h2" sx={{ ...sectionHeadingSx, mb: 1.25 }}>
                See it on your own deck.
              </Typography>
              <Typography sx={{ ...bodySx, maxWidth: 600, mx: 'auto', mb: 2.5 }}>
                Bring a real slide from your workflow. We'll rebuild it live, inside
                PowerPoint, on your brand.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <DemoCta label="See it on your own deck" />
              </Box>
              <Typography
                sx={{
                  mt: 2.5,
                  fontFamily: BRAND_SANS_FONT,
                  fontSize: '0.8rem',
                  color: alpha(INK, 0.5),
                }}
              >
                Runs on Claude by default, with GPT and Gemini a click away.
                Works inside your real PowerPoint.
              </Typography>
            </Box>
          </Reveal>
        </Box>
      </Box>

      <Footer />
    </LandingContainer>
  );
}
